'use client';

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiList, FiX } from 'react-icons/fi';
import { Heading } from '@/lib/blog/blog-utils';

export default function MobileTOC({ headings }: { headings: Heading[] }) {
  const [isOpen, setIsOpen] = useState(false);

  // Prevent background scrolling when drawer is active
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  if (headings.length === 0) return null;

  return (
    <div className="xl:hidden"> {/* Changed lg:hidden to xl:hidden to match your desktop TOC visibility */}
      {/* Floating Action Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-40 p-4 bg-indigo-600 text-white rounded-full shadow-2xl hover:scale-110 active:scale-95 transition-all border border-white/20"
        aria-label="Open Table of Contents"
      >
        <FiList size={24} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-zinc-950/60 backdrop-blur-sm z-50"
            />
            
            {/* Slide-out Drawer */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 h-full w-[85vw] max-w-sm bg-white dark:bg-zinc-950 z-[60] p-8 shadow-2xl border-l border-zinc-100 dark:border-zinc-900 flex flex-col"
            >
              <div className="flex justify-between items-center mb-10">
                <div className="space-y-1">
                  <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400">
                    Table of Contents
                  </h3>
                  <p className="text-[9px] font-medium text-zinc-500 uppercase">Jump to section</p>
                </div>
                <button 
                  onClick={() => setIsOpen(false)} 
                  className="p-3 bg-zinc-100 dark:bg-zinc-900 text-zinc-500 dark:text-zinc-400 rounded-xl"
                >
                  <FiX size={20} />
                </button>
              </div>

              <nav className="flex-1 overflow-y-auto">
                <ul className="space-y-6 border-l border-zinc-100 dark:border-zinc-900">
                  {headings.map((h) => (
                    <li 
                      key={h.id} 
                      style={{ paddingLeft: h.level === 3 ? '2rem' : '1.5rem' }}
                      className="relative"
                    >
                      {/* Hierarchical indicator */}
                      <div className="absolute left-[-1.5px] top-1/2 -translate-y-1/2 w-[3px] h-[3px] bg-zinc-300 dark:bg-zinc-700 rounded-full" />
                      
                      <a
                        href={`#${h.id}`}
                        onClick={() => setIsOpen(false)}
                        className="text-sm font-semibold text-zinc-700 dark:text-zinc-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors block leading-snug"
                      >
                        {h.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className="mt-8 pt-8 border-t border-zinc-100 dark:border-zinc-900">
                 <button 
                   onClick={() => setIsOpen(false)}
                   className="w-full py-4 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-xl text-xs font-bold uppercase tracking-widest"
                 >
                   Close Menu
                 </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}